/**
 * The public page address, which has its own asynchronous contract and needs its own hook.
 *
 * TWO CALLS, ONE OPERATION. `checkSlugAvailability` WITH a `slug` asks "may I have this one";
 * WITHOUT it asks only for a SUGGESTION derived from the display name. The second form is where
 * transliteration lives, and it is the only path an author writing in a non-Latin script has to a
 * valid address at all — the ruled shape is lowercase Latin letters, digits and single hyphens.
 *
 * A FREE ANSWER RESERVES NOTHING. The address is claimed when the profile saves, so an "available"
 * here is a courtesy and the save can still be refused. The UI says so once rather than implying
 * a hold it does not have.
 *
 * DEBOUNCE HARD. The endpoint is rate limited (429), so the lever is asking less often: 400ms
 * after the last keystroke, and never for a value that cannot pass the shape rule anyway. After a
 * 429 the next check waits that 429's own `Retry-After` when it carried one, and the field says
 * how long; a 429 without one backs off on the fixed schedule (`backoffFor`) as it always did.
 *
 * ★ THE SUGGESTION IS BUILT FROM THE SAVED NAME, SO AN ANSWER WITH NONE IS NEVER LATCHED (design 14,
 * finding F7). The server transliterates the display name it HOLDS (`slugifyDisplayName(head
 * ?.displayName)`, src/modules/authors/services/slug-availability.service.ts), not the one being
 * typed, and answers `suggestion: null` when it holds none. The cabinet walk of 2026-10-01 pressed
 * "Suggest one from my name" before saving the name: the first answer was null, the hook latched
 * anyway, and every later press returned at the latch - a dead button with no word on screen until
 * a reload. Now a null answer leaves the button live and says why (`missingSuggestion`): the name is
 * not saved yet, or - the rare case - it is saved and gave nothing usable (no Latin letters to
 * make, or every candidate taken). A change to the saved name drops the latch and any earlier
 * answer, because a suggestion from the old name is a suggestion for somebody else.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { SlugAvailability } from '../contract';
import { ApiError, backoffFor, isRateLimited } from '../api/problem';
import { LIMITS, SLUG_SHAPE } from '../domain/limits';
import { useApplicationApi } from './provider';

const DEBOUNCE_MS = 400;

export type SlugCheckPhase =
  'idle' | 'too_short' | 'bad_shape' | 'checking' | 'answered' | 'failed';

export interface SlugCheck {
  readonly phase: SlugCheckPhase;
  readonly result: SlugAvailability | undefined;
  /** True when the last failure was a 429 — the wording differs, and so does the advice. */
  readonly rateLimited: boolean;
  /** The last 429's `Retry-After` in seconds, when it carried one. The field says the wait. */
  readonly retryAfterSeconds: number | undefined;
  /** A candidate the server transliterated from the display name, once asked for. */
  readonly suggestion: string | undefined;
  /** Why the last answer carried no suggestion - `null` when it carried one, or none was asked. */
  readonly missingSuggestion: MissingSuggestion | null;
  readonly suggestionLoading: boolean;
  readonly requestSuggestion: () => void;
}

/**
 * `name_not_saved`: the profile holds no display name, so the server had nothing to build from.
 * `name_gave_none`: it holds one, and no free address came of it.
 */
export type MissingSuggestion = 'name_not_saved' | 'name_gave_none';

/**
 * `savedName` is the display name the PROFILE holds (`OwnProfile.displayName`, the last save), not
 * the draft being typed - the server suggests from the saved one, so that is the one that says why
 * an answer came back empty. `null` while nothing is saved.
 */
export function useSlugCheck(value: string, enabled: boolean, savedName: string | null): SlugCheck {
  const api = useApplicationApi();
  const [phase, setPhase] = useState<SlugCheckPhase>('idle');
  const [result, setResult] = useState<SlugAvailability | undefined>(undefined);
  const [rateLimited, setRateLimited] = useState(false);
  const [retryAfterSeconds, setRetryAfterSeconds] = useState<number | undefined>(undefined);
  const [suggestion, setSuggestion] = useState<string | undefined>(undefined);
  const [missingSuggestion, setMissingSuggestion] = useState<MissingSuggestion | null>(null);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const askedForSuggestion = useRef(false);
  // Read when an answer lands, so an empty answer is explained by the name held at THAT moment.
  const nameHeld = useRef(savedName);
  // How many 429s in a row, and the wait the last one named. The next check waits that 429's own
  // `Retry-After` (declared on all twenty of authors-api's 429s since decision-log row 220) when it
  // carried one - never less than the debounce - and our fixed schedule (`backoffFor`) when not.
  const throttleStreak = useRef(0);
  const namedWaitMs = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!enabled) {
      setPhase('idle');
      return;
    }
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      setPhase('idle');
      setResult(undefined);
      return;
    }
    // Refuse locally what the server would refuse anyway. This is not a second opinion — it is
    // simply not spending one of a rate-limited endpoint's requests on a value we can already see
    // is out of shape.
    if (trimmed.length < LIMITS.slug.min || trimmed.length > LIMITS.slug.max) {
      setPhase('too_short');
      setResult(undefined);
      return;
    }
    if (!SLUG_SHAPE.test(trimmed)) {
      setPhase('bad_shape');
      setResult(undefined);
      return;
    }

    const controller = new AbortController();
    // After a 429 the wait grows, so a person holding down a key does not walk into the limit
    // again the instant they stop.
    const wait =
      throttleStreak.current === 0
        ? DEBOUNCE_MS
        : (namedWaitMs.current ?? backoffFor(throttleStreak.current - 1));
    const timer = setTimeout(() => {
      setPhase('checking');
      setRateLimited(false);
      api
        .checkSlugAvailability(trimmed, { signal: controller.signal })
        .then((answer) => {
          throttleStreak.current = 0;
          setResult(answer);
          setPhase('answered');
        })
        .catch((cause: unknown) => {
          if (cause instanceof DOMException && cause.name === 'AbortError') return;
          const throttled = isRateLimited(cause);
          const seconds =
            throttled && cause instanceof ApiError ? cause.retryAfterSeconds : undefined;
          throttleStreak.current = throttled ? throttleStreak.current + 1 : 0;
          namedWaitMs.current =
            seconds === undefined ? undefined : Math.max(DEBOUNCE_MS, seconds * 1000);
          setRateLimited(throttled);
          setRetryAfterSeconds(seconds);
          setPhase('failed');
        });
    }, wait);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [api, value, enabled]);

  // A different saved name is a different suggestion: drop the latch and whatever the old name gave.
  useEffect(() => {
    nameHeld.current = savedName;
    askedForSuggestion.current = false;
    setSuggestion(undefined);
    setMissingSuggestion(null);
  }, [savedName]);

  const requestSuggestion = useCallback(() => {
    if (askedForSuggestion.current) return;
    askedForSuggestion.current = true;
    setSuggestionLoading(true);
    api
      .checkSlugAvailability(undefined)
      .then((answer) => {
        const candidate = answer.suggestion ?? '';
        if (candidate.length === 0) {
          // NOT latched: the next press asks again - after a save it will have a name to work from.
          askedForSuggestion.current = false;
          setSuggestion(undefined);
          const held = nameHeld.current;
          setMissingSuggestion(
            held === null || held.trim().length === 0 ? 'name_not_saved' : 'name_gave_none',
          );
          return;
        }
        setSuggestion(candidate);
        setMissingSuggestion(null);
      })
      .catch(() => {
        // A missing suggestion is not worth an error: the field still works by hand. Allow
        // another attempt later rather than latching the failure.
        askedForSuggestion.current = false;
      })
      .finally(() => {
        setSuggestionLoading(false);
      });
  }, [api]);

  return {
    phase,
    result,
    rateLimited,
    retryAfterSeconds,
    suggestion,
    missingSuggestion,
    suggestionLoading,
    requestSuggestion,
  };
}
